import React, { useEffect, useState } from 'react';
import { CheckCircle, X, Mail, Phone, FileText, Send } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Beautiful centered confirmation modal for form submissions
 * Much more stylish than standard toasts!
 */
const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  type = 'success', // 'success', 'callback', 'quote', 'brochure', 'contact'
  title,
  message,
  userName,
  autoCloseDelay = 5000 // Auto close after 5 seconds
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (isOpen) {
      // Trigger animation
      setTimeout(() => setIsVisible(true), 10);
      
      // Start progress bar countdown
      const startTime = Date.now();
      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 100 - (elapsed / autoCloseDelay) * 100);
        setProgress(remaining);
        
        if (remaining <= 0) {
          clearInterval(interval);
          handleClose();
        }
      }, 50);

      return () => clearInterval(interval);
    } else {
      setIsVisible(false);
      setProgress(100);
    }
  }, [isOpen, autoCloseDelay]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(), 300);
  };

  if (!isOpen) return null;

  // Determine icon and colors based on type
  const getConfig = () => {
    switch (type) {
      case 'callback':
        return {
          icon: Phone,
          gradient: 'from-emerald-500 to-teal-600',
          bgGlow: 'bg-emerald-500/20',
          iconBg: 'bg-emerald-500',
          title: title || 'Demande de rappel envoyée !',
          message: message || `Merci ${userName} ! Notre équipe vous contactera très rapidement.`
        };
      case 'quote':
        return {
          icon: FileText,
          gradient: 'from-cyan-500 to-blue-600',
          bgGlow: 'bg-cyan-500/20',
          iconBg: 'bg-cyan-500',
          title: title || 'Demande de devis envoyée !',
          message: message || `Merci ${userName} ! Nous reviendrons vers vous sous 24h avec une proposition personnalisée.`
        };
      case 'brochure':
        return {
          icon: FileText,
          gradient: 'from-purple-500 to-indigo-600',
          bgGlow: 'bg-purple-500/20',
          iconBg: 'bg-purple-500',
          title: title || 'Brochure envoyée !',
          message: message || `Merci ${userName} ! La brochure s'ouvre dans un nouvel onglet et vous recevrez un email de confirmation.`
        };
      case 'contact':
        return {
          icon: Mail,
          gradient: 'from-blue-500 to-indigo-600',
          bgGlow: 'bg-blue-500/20',
          iconBg: 'bg-blue-500',
          title: title || 'Message envoyé !',
          message: message || `Merci ${userName} ! Nous avons bien reçu votre message et vous répondrons rapidement.`
        };
      default:
        return {
          icon: CheckCircle,
          gradient: 'from-emerald-500 to-cyan-600',
          bgGlow: 'bg-emerald-500/20',
          iconBg: 'bg-emerald-500',
          title: title || 'Envoyé avec succès !',
          message: message || `Merci ${userName} ! Votre demande a bien été enregistrée.`
        };
    }
  };

  const config = getConfig();
  const IconComponent = config.icon;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className={cn(
          "absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300",
          isVisible ? "opacity-100" : "opacity-0"
        )}
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div 
        className={cn(
          "relative w-full max-w-md transform transition-all duration-300 ease-out",
          isVisible 
            ? "opacity-100 scale-100 translate-y-0" 
            : "opacity-0 scale-95 translate-y-4"
        )}
      >
        {/* Glow effect */}
        <div className={cn("absolute -inset-1 rounded-3xl blur-xl opacity-50", config.bgGlow)} />
        
        {/* Card */}
        <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Progress bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-slate-100">
            <div 
              className={cn("h-full bg-gradient-to-r transition-all duration-100", config.gradient)}
              style={{ width: `${progress}%` }}
            />
          </div>
          
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-1 rounded-full hover:bg-slate-100 transition-colors z-10"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
          
          {/* Content */}
          <div className="pt-10 pb-8 px-8 text-center">
            {/* Animated icon */}
            <div className="relative mx-auto w-20 h-20 mb-6">
              {/* Ripple effect */}
              <div className={cn(
                "absolute inset-0 rounded-full animate-ping opacity-25",
                config.iconBg
              )} />
              <div className={cn(
                "absolute inset-2 rounded-full animate-pulse opacity-50",
                config.iconBg
              )} />
              
              {/* Icon container */}
              <div className={cn(
                "relative w-full h-full rounded-full flex items-center justify-center bg-gradient-to-br shadow-lg",
                config.gradient
              )}>
                <IconComponent className="w-10 h-10 text-white" strokeWidth={2} />
              </div>
            </div>
            
            {/* Title */}
            <h3 className={cn(
              "text-2xl font-bold mb-3 bg-gradient-to-r bg-clip-text text-transparent",
              config.gradient
            )}>
              {config.title}
            </h3>
            
            {/* Message */}
            <p className="text-slate-600 text-base leading-relaxed mb-6">
              {config.message}
            </p>
            
            {/* Action button */}
            <button
              onClick={handleClose}
              className={cn(
                "w-full py-3 px-6 rounded-xl font-semibold text-white shadow-lg",
                "transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200",
                "bg-gradient-to-r",
                config.gradient
              )}
            >
              Parfait, merci !
            </button>
            
            {/* Subtle note */}
            <p className="mt-4 text-xs text-slate-400">
              Un email de confirmation vous sera envoyé
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
